/**
 * The keyboard, in one place.
 *
 * These lists were previously duplicated — the real bindings in `main.ts`, the
 * labels drawn on the buttons in `hud.ts` — with a comment on each asking the
 * next person to keep them in step. They did not stay in step: moving the
 * Sovereign Powers off W/A/S/D (which pan the camera, and so were arming
 * powers every time the player looked around the map) left the HUD still
 * printing "W" on a button that had become "E". The player is told to press a
 * key that does something else.
 *
 * One list, imported by both. A binding change is now a one-line change and the
 * labels cannot lie.
 */

/**
 * Sovereign Powers.
 *
 * Deliberately avoids W, A, S and D — those pan the camera. Also steers clear
 * of F/G/H/V/B (company orders), M (mute), P (pause), Tab (realm/city) and the
 * digits (hotkey groups).
 */
export const POWER_HOTKEYS: readonly string[] = ['q', 'e', 'r', 't', 'y', 'u', 'z', 'x', 'c', 'n'];

/**
 * Hero battle skills, on the number row.
 *
 * The digits are the front-of-house keys in a game like this and the hero's
 * skills are the thing the player reaches for most, so they get them. The slot
 * a key fires is fixed by the level that unlocks it — see `heroAbilitySlots` —
 * rather than by the order skills were bought, so `3` means the same skill for
 * the whole run, and shows what it will be, and at what level, before it is
 * learned.
 *
 * Company hotkey groups moved off the bare digits to make room: Ctrl+digit
 * still sets a group, Shift+digit now recalls one.
 */
export const HERO_HOTKEYS: readonly string[] = ['1', '2', '3', '4', '5'];

/**
 * Everything else, which used to be string literals scattered through
 * `main.ts`'s keydown and a second, drifting copy in the title screen's help.
 *
 * The powers and the hero skills were moved here for exactly this reason and
 * then the rest of the keyboard stayed where it was. It is here now because the
 * HUD prints these on buttons: a label typed out by hand beside a binding read
 * from a variable is the same bug in a new place, and this file's header is the
 * story of that bug.
 */
export const KEY_SHEET = 'i';
export const KEY_LEDGER = 'l';
/**
 * The Levies. K, because it is the letter next to the Ledger's and the two are
 * the crown's two account books — and because everything else a player reaches
 * for is spoken for: the camera has WASD and the arrows, the Powers have
 * Q/E/R/T/Y/U/Z/X/C/N, the company orders have F/G/H/V/B, and I, L, M and P are
 * the four screens.
 */
export const KEY_LEVIES = 'k';
export const KEY_HORN = ' ';
export const KEY_PAUSE = 'p';
export const KEY_MUTE = 'm';
/** Realm ⇄ city in a campaign; tactical ⇄ close zoom outside one. */
export const KEY_SWAP = 'tab';
export const KEY_FASTER = '+';
export const KEY_SLOWER = '-';
export const KEY_FORMATION = 'f';
export const KEY_STANCE = 'g';
export const KEY_HOLD = 'h';
export const KEY_SHIELDWALL = 'v';
export const KEY_RALLY = 'b';
export const KEY_STATS = 'f3';
export const KEY_CONSOLE = '`';
export const KEY_SAVE = 'f5';
export const KEY_LOAD = 'f9';

/** A binding as it should be printed on a button. */
export function keyLabel(k: string): string {
  if (k === ' ') return 'Space';
  if (k === 'tab') return 'Tab';
  if (k === '`') return '`';
  if (k.length > 1) return k.toUpperCase();
  return k.toUpperCase();
}

/** The same keys as the player sees them printed on a button. */
export const POWER_KEY_LABELS = POWER_HOTKEYS.map(keyLabel);
export const HERO_KEY_LABELS = HERO_HOTKEYS.map(keyLabel);

/**
 * The whole keyboard, for the help sheet. Grouped the way a player thinks
 * about them rather than the way `main.ts` happens to test them.
 */
export const KEY_HELP: Array<{ group: string; rows: Array<[string, string]> }> = [
  {
    group: 'The run',
    rows: [
      [keyLabel(KEY_HORN), 'Sound the horn — call the wave in now'],
      [keyLabel(KEY_PAUSE), 'Pause'],
      [`${KEY_SLOWER} / ${KEY_FASTER}`, 'Speed — 0.5, 1, 2, 4, 8'],
      [keyLabel(KEY_SWAP), 'Realm ⇄ city (or snap the zoom, outside a campaign)'],
      [keyLabel(KEY_SAVE), 'Save'],
      [keyLabel(KEY_LOAD), 'Load'],
      [keyLabel(KEY_MUTE), 'Sound off'],
    ],
  },
  {
    group: 'Screens',
    rows: [
      [keyLabel(KEY_SHEET), 'The character sheet — skills and kit'],
      [keyLabel(KEY_LEDGER), 'The Ledger — tax, powers, people, production'],
      [keyLabel(KEY_LEVIES), 'The Levies — where your men come from (on the realm map)'],
      [keyLabel(KEY_STATS), 'Frame and unit counts'],
      [keyLabel(KEY_CONSOLE), 'Debug console'],
      ['Esc', 'Close whatever is open; drop the selection'],
    ],
  },
  {
    group: 'Companies',
    rows: [
      ['Left-click', 'Select · shift-click adds · drag a box for several'],
      ['Right-click', 'Move there · shift for an attack-move'],
      ['Right-drag', 'Set the frontage and the facing'],
      [keyLabel(KEY_FORMATION), 'Cycle formation'],
      [keyLabel(KEY_STANCE), 'Cycle stance'],
      [keyLabel(KEY_HOLD), 'Hold'],
      [keyLabel(KEY_SHIELDWALL), 'Shieldwall'],
      [keyLabel(KEY_RALLY), 'Rally'],
      ['Ctrl + 1…9', 'Set a group'],
      ['Shift + 1…9', 'Recall a group'],
    ],
  },
  {
    group: 'Powers and the hero',
    rows: [
      [POWER_KEY_LABELS.join(' '), 'Sovereign Powers, in the order the bar shows them'],
      [HERO_KEY_LABELS.join(' '), "The hero's battle skills"],
      ['W A S D', 'Pan the camera · arrows do the same'],
      ['Wheel', 'Zoom'],
    ],
  },
  /**
   * Fingers.
   *
   * This sheet is the game's only account of how it is driven, and until the
   * touch layer landed it described a machine half the people who open it do
   * not have: every row above names a key or a mouse button. The gestures are
   * not a second control scheme — each one *becomes* the mouse event beside it,
   * in `ui/touch.ts` — so they belong on the same sheet rather than on a
   * separate one that would drift from it.
   *
   * Listed last because a player with a keyboard should not read past it, and
   * kept here rather than in `hud.ts` for the reason the rest of this file
   * exists: a control written down in one place and implemented in another is a
   * control that ends up lying.
   */
  {
    group: 'Fingers',
    rows: [
      ['Tap', 'Left-click — select, place, cast'],
      ['Press and hold', 'Right-click — move there, or put an armed tool away'],
      ['Drag', 'Pan the map · draws the line instead when a wall or road is armed'],
      ['Pinch', 'Zoom, about the point between your fingers'],
      ['Hold a panel', 'Open its tooltip. The press does not also press the button'],
      ['Menu', 'The fifth tab is Escape: close, let go, and then Stand Down'],
    ],
  },
];
